import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Car, Navigation, Phone, Mail, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LOCATIONS = [
  {
    id: 'rycom',
    name: 'OCEANS 13 & DEMOLITION ABDUCTION',
    subtitle: 'Rycom Mall · 5th Floor',
    address: '〒901-2306 Okinawa, Kitanakagusuku, Raikamu 1',
    addressJa: '〒901-2306 沖縄県北中城村ライカム1',
    directions: [
      'Located on the 5th Floor of AEON Mall Okinawa Rycom',
      'Best to park on the 5th Floor for direct access to Oki-Escape',
      'On rainy days, park internally and take the escalators up to the 5th floor',
    ],
    directionsJa: [
      'イオンモール沖縄ライカムの5階にあります',
      '5階に直接アクセスできるよう、5階に駐車するのがベストです',
      '雨の日は館内に駐車し、エスカレーターで5階へお越しください',
    ],
    parking: 'Free mall parking — 5th floor recommended for direct access',
    parkingJa: '無料モール駐車場 — 5階がおすすめ',
    mapUrl: 'https://www.google.com/maps/place/%E3%82%A4%E3%82%AA%E3%83%B3%E3%83%A2%E3%83%BC%E3%83%AB%E6%B2%96%E7%B8%84%E3%83%A9%E3%82%A4%E3%82%AB%E3%83%A0/@26.3137504,127.7944138,17z',
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3579.5!2d127.7944138!3d26.3137504!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x34e5126214d2f427%3A0xcb2e803176e4f16b!2z44Kk44Kq44Oz44Oi44O844Or5rKW57iE44Op44Kk44Kr44Og!5e0!3m2!1sen!2sjp!4v1700000000000!5m2!1sen!2sjp',
    rooms: ['Oceans 13 (Bank Heist)', 'Demolition Abduction'],
    accent: '#E32510',
  },
  {
    id: 'pirates',
    name: 'PIRATE SHIP',
    subtitle: 'Okinawa City · Gate 2 Street',
    address: '〒904-0004 Okinawa, Chūō, 1-chōme-10 (コザ花園)',
    addressJa: '〒904-0004 沖縄県沖縄市中央1丁目10（コザ花園）',
    directions: [
      'Located just off Gate 2 Street near Kadena Air Force Base',
      'Enter Sun City outdoor shopping plaza across from Koza Music Town',
      'Walk straight until the first intersection, then turn left — you\'ve arrived!',
      'Directly across from Tequila Bar & Grill Mexican Restaurant',
    ],
    directionsJa: [
      '嘉手納空軍基地近くのゲート2ストリート沿い',
      'コザミュージックタウン向かいのサンシティショッピングプラザに入ります',
      'まっすぐ歩いて最初の交差点を左折すれば到着です！',
      'テキーラバー＆グリル メキシカンレストランの真向かいです',
    ],
    parking: 'Center Parking Lot recommended',
    parkingJa: 'センター駐車場がおすすめ',
    parkingUrl: 'https://goo.gl/maps/F2zdutEdRMBgEwey6',
    mapUrl: 'https://www.google.com/maps/dir/?api=1&destination=26.336287297606,127.79963372387',
    embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1789.5!2d127.7996337!3d26.3362873!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDIwJzEwLjYiTiAxMjfCsDQ3JzU4LjciRQ!5e0!3m2!1sen!2sjp!4v1700000000000!5m2!1sen!2sjp',
    rooms: ['Pirate Ship'],
    accent: '#d4a017',
  },
];

export default function DirectionsPage() {
  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const [roomSettings, setRoomSettings] = useState({ oceans13: true, demolition: true, pirate: true, sweet_escape: false });

  useEffect(() => {
    axios.get(`${API}/api/settings/rooms`).then(r => setRoomSettings(r.data)).catch(() => {});
  }, []);

  // Build Rycom rooms list based on visibility
  const rycomRooms = [];
  if (roomSettings.oceans13) rycomRooms.push(isJa ? 'オーシャンズ13（銀行強盗）' : 'Oceans 13 (Bank Heist)');
  if (roomSettings.demolition) rycomRooms.push(isJa ? 'デモリッション・アブダクション' : 'Demolition Abduction');
  if (roomSettings.sweet_escape) rycomRooms.push(isJa ? 'ベイク・ミー・ハッピー（2〜6名）' : 'Bake Me Happy (2–6)');

  const pirateRooms = roomSettings.pirate ? [isJa ? '海賊船' : 'Pirate Ship'] : [];

  const locations = LOCATIONS.map(loc => {
    if (loc.id === 'rycom') return { ...loc, rooms: rycomRooms };
    if (loc.id === 'pirates') return { ...loc, rooms: pirateRooms };
    return loc;
  }).filter(loc => loc.rooms.length > 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <div className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link
            to="/"
            data-testid="directions-back-link"
            className="flex items-center gap-2 text-muted-foreground hover:text-brand font-heading text-xs tracking-widest uppercase transition-colors"
          >
            <ArrowLeft size={14} /> {isJa ? 'ホームに戻る' : 'Back to OkiEscape'}
          </Link>
          <Link
            to="/faq"
            data-testid="directions-faq-link"
            className="text-muted-foreground hover:text-brand font-heading text-xs tracking-widest uppercase transition-colors"
          >
            {isJa ? 'よくある質問' : 'FAQ'}
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 text-brand font-heading text-xs tracking-[0.25em] uppercase mb-4">
            <Navigation size={14} />
            <span>{isJa ? 'アクセス' : 'Directions'}</span>
          </div>
          <h1
            data-testid="directions-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-wide uppercase"
            style={{ textShadow: '0 0 40px rgba(227,37,16,0.15)' }}
          >
            {isJa ? 'OKI-ESCAPEへの行き方' : 'Find OKI-ESCAPE'}
          </h1>
          <p className="font-sans text-base text-muted-foreground mt-3 max-w-xl mx-auto">
            {isJa
              ? '2つのロケーション — ご予約の場所を確認してからお越しください'
              : 'Two locations across Okinawa — make sure you know which one you booked!'}
          </p>
        </div>
      </div>

      {/* Important notice */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-10">
        <div
          data-testid="directions-notice"
          className="border-2 border-brand/40 bg-brand/5 rounded-2xl p-5 text-center"
        >
          <p className="font-heading text-sm sm:text-base tracking-wider uppercase text-brand">
            {isJa
              ? 'ご予約の場所をご確認ください'
              : 'Please ensure you understand which location you have booked'}
          </p>
          <p className="font-sans text-sm text-muted-foreground mt-2">
            {isJa
              ? 'オーシャンズ13 & デモリッション・アブダクション → ライカムモール ／ 海賊船 → 沖縄市'
              : 'Oceans 13 & Demolition Abduction → Rycom Mall ／ Pirates → Okinawa City'}
          </p>
        </div>
      </div>

      {/* Location cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 space-y-10">
        {locations.map(loc => (
          <LocationCard key={loc.id} loc={loc} isJa={isJa} />
        ))}
      </div>

      {/* Contact strip */}
      <div className="border-t border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-center">
          <p className="font-heading text-lg sm:text-xl tracking-wider uppercase mb-4">
            {isJa ? 'まだ迷っていますか？' : 'Still confused? Contact us!'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5">
            <a
              href="mailto:info@okiescape.com"
              data-testid="directions-email"
              className="inline-flex items-center gap-2 font-sans text-sm text-muted-foreground hover:text-brand transition-colors"
            >
              <Mail size={15} /> info@okiescape.com
            </a>
            <a
              href="tel:098-989-0319"
              data-testid="directions-phone"
              className="inline-flex items-center gap-2 font-sans text-sm text-muted-foreground hover:text-brand transition-colors"
            >
              <Phone size={15} /> 098-989-0319
            </a>
            <a
              href="http://facebook.com/okiescape"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-facebook"
              className="inline-flex items-center gap-2 font-sans text-sm text-muted-foreground hover:text-brand transition-colors"
            >
              <ExternalLink size={15} /> Facebook
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationCard({ loc, isJa }) {
  return (
    <div
      data-testid={`location-card-${loc.id}`}
      className="rounded-2xl border border-border bg-card overflow-hidden"
    >
      {/* Header band */}
      <div
        className="px-6 py-5 border-b border-border"
        style={{ background: `linear-gradient(135deg, ${loc.accent}12, transparent)` }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-wider uppercase" style={{ color: loc.accent }}>
              {loc.name}
            </h2>
            <p className="font-sans text-sm text-muted-foreground mt-1">{loc.subtitle}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {loc.rooms.map(r => (
              <span key={r} className="font-heading text-[11px] tracking-widest uppercase px-3 py-1 rounded-full border border-border bg-secondary/50 text-foreground">
                {r}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 grid lg:grid-cols-2 gap-8">
        {/* Left: info */}
        <div className="space-y-6">
          {/* Address */}
          <div>
            <div className="flex items-center gap-2 text-brand mb-2">
              <MapPin size={16} />
              <span className="font-heading text-sm tracking-widest uppercase">{isJa ? '住所' : 'Address'}</span>
            </div>
            <p className="font-sans text-sm text-foreground">{isJa ? loc.addressJa : loc.address}</p>
          </div>

          {/* Step-by-step directions */}
          <div>
            <div className="flex items-center gap-2 text-brand mb-2">
              <Navigation size={16} />
              <span className="font-heading text-sm tracking-widest uppercase">{isJa ? '行き方' : 'How to Get There'}</span>
            </div>
            <ol className="space-y-2">
              {(isJa ? loc.directionsJa : loc.directions).map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-heading text-lg leading-tight text-brand/70">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-sans text-sm text-foreground/90 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Parking */}
          <div>
            <div className="flex items-center gap-2 text-brand mb-2">
              <Car size={16} />
              <span className="font-heading text-sm tracking-widest uppercase">{isJa ? '駐車場' : 'Parking'}</span>
            </div>
            <p className="font-sans text-sm text-foreground/90">{isJa ? loc.parkingJa : loc.parking}</p>
            {loc.parkingUrl && (
              <a
                href={loc.parkingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`parking-link-${loc.id}`}
                className="inline-flex items-center gap-1.5 mt-2 font-heading text-xs tracking-widest uppercase text-brand hover:underline"
              >
                <ExternalLink size={12} /> {isJa ? '駐車場の地図' : 'View Parking Map'}
              </a>
            )}
          </div>

          {/* Google Maps button */}
          <a
            href={loc.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`maps-link-${loc.id}`}
            className="inline-flex items-center gap-2 font-heading text-sm tracking-widest uppercase bg-brand text-white px-5 py-3 rounded-xl hover:bg-brand-hover transition-colors"
          >
            <Navigation size={15} /> {isJa ? 'Google マップで開く' : 'Open in Google Maps'}
          </a>
        </div>

        {/* Right: embedded map */}
        <div className="rounded-xl overflow-hidden border border-border bg-secondary/20 min-h-[300px]">
          <iframe
            title={`Map - ${loc.name}`}
            src={loc.embedUrl}
            className="w-full h-full min-h-[300px]"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}
